import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
// @ts-ignore
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { ContractTemplate, CreateContractTemplateData } from '../../types/contracts';

interface ContractTemplatesProps {
  onSelectTemplate?: (template: ContractTemplate) => void;
}

const ContractTemplates: React.FC<ContractTemplatesProps> = ({ onSelectTemplate }) => {
  const [templates, setTemplates] = useState<ContractTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<ContractTemplate | null>(null);
  const [newTemplate, setNewTemplate] = useState<CreateContractTemplateData>({
    name: '',
    description: '',
    content: '',
    variables: [],
    is_default: false
  });
  const [newTemplateQuillRef, setNewTemplateQuillRef] = useState<any>(null);
  const [editTemplateQuillRef, setEditTemplateQuillRef] = useState<any>(null);

  // Rich text editor configuration for contract templates
  const quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'header': [1, 2, 3, false] }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['link'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['clean']
    ],
  };

  const quillFormats = [
    'bold', 'italic', 'underline', 'header', 'list', 'bullet',
    'link', 'color', 'background', 'align'
  ];

  useEffect(() => {
    fetchTemplates();
  }, []);

  // Handle ESC key press
  const handleEscKey = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      if (showCreateModal) {
        setShowCreateModal(false);
      }
      if (editingTemplate) {
        setEditingTemplate(null);
      }
    }
  }, [showCreateModal, editingTemplate]);

  useEffect(() => {
    // Add event listener when modals are open
    if (showCreateModal || editingTemplate) {
      document.addEventListener('keydown', handleEscKey);
    }
    
    // Cleanup event listener
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showCreateModal, editingTemplate, handleEscKey]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/contracts/templates', {
        headers: { 'x-auth-token': token }
      });
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching contract templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/contracts/templates', newTemplate, {
        headers: { 'x-auth-token': token }
      });
      
      setTemplates([...templates, response.data]);
      setShowCreateModal(false);
      setNewTemplate({
        name: '',
        description: '',
        content: '',
        variables: [],
        is_default: false
      });
      alert('Template created successfully!');
    } catch (error: any) {
      console.error('Error creating contract template:', error);
      alert('Failed to create template: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    }
  };

  const handleUpdateTemplate = async () => {
    if (!editingTemplate) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`/api/contracts/templates/${editingTemplate.id}`, editingTemplate, {
        headers: { 'x-auth-token': token }
      });
      
      setTemplates(templates.map(t => t.id === editingTemplate.id ? response.data : t));
      setEditingTemplate(null);
      alert('Template updated successfully!');
    } catch (error: any) {
      console.error('Error updating contract template:', error);
      alert('Failed to update template: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    }
  };

  const handleDeleteTemplate = async (templateId: number) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/contracts/templates/${templateId}`, {
        headers: { 'x-auth-token': token }
      });
      
      setTemplates(templates.filter(t => t.id !== templateId));
    } catch (error) {
      console.error('Error deleting contract template:', error);
    }
  };

  const parseVariables = (content: string): string[] => {
    const matches = content.match(/{{(.*?)}}/g);
    if (!matches) return [];
    
    return Array.from(new Set(matches.map(match => match.slice(2, -2))));
  };

  const updateTemplateContent = (content: string) => {
    const variables = parseVariables(content);
    if (editingTemplate) {
      setEditingTemplate({ ...editingTemplate, content, variables });
    } else {
      setNewTemplate({ ...newTemplate, content, variables });
    }
  };

  // Common variables that can be inserted
  const commonVariables = [
    { name: 'current_date', label: 'Current Date' },
    { name: 'lead_name', label: 'Customer Name' },
    { name: 'lead_email', label: 'Customer Email' },
    { name: 'lead_company', label: 'Customer Company' },
    { name: 'deal_title', label: 'Deal Title' },
    { name: 'deal_value', label: 'Deal Value' },
    { name: 'deal_currency', label: 'Deal Currency' },
    { name: 'company_name', label: 'Your Company Name' },
    { name: 'custom_note', label: 'Custom Note' }
  ];

  const insertVariable = (variableName: string, isEditing: boolean = false) => {
    const quillRef = isEditing ? editTemplateQuillRef : newTemplateQuillRef;
    if (!quillRef) return;

    const quill = quillRef.getEditor();
    const range = quill.getSelection();
    const index = range ? range.index : quill.getLength();
    
    // Insert the variable at cursor position
    quill.insertText(index, `{{${variableName}}}`, 'bold', true);
    
    // Update the content
    const newContent = quill.root.innerHTML;
    updateTemplateContent(newContent);
    
    // Move cursor after the inserted variable
    quill.setSelection(index + `{{${variableName}}}`.length);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-gray-900">Contract Templates</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
        >
          Create Template
        </button>
      </div>

      {/* Templates List */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Available Templates</h3>
        </div>
        
        {templates.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            No contract templates found. Create your first template to get started.
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {templates.map((template) => (
              <div key={template.id} className="p-6 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex-grow">
                    <div className="flex items-center space-x-3">
                      <h4 className="text-lg font-medium text-gray-900">{template.name}</h4>
                      {template.is_default && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          Default
                        </span>
                      )}
                    </div>
                    {template.description && (
                      <p className="mt-1 text-sm text-gray-500">{template.description}</p>
                    )}
                    <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                      <span>{template.variables.length} variables</span>
                      <span>Created {new Date(template.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {onSelectTemplate && (
                      <button
                        onClick={() => onSelectTemplate(template)}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        Use Template
                      </button>
                    )}
                    <button
                      onClick={() => setEditingTemplate(template)}
                      className="text-gray-600 hover:text-gray-800"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    {!template.is_default && (
                      <button
                        onClick={() => handleDeleteTemplate(template.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create Template Modal */}
      {showCreateModal && (
        <div 
          className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
          onClick={() => setShowCreateModal(false)}
        >
          <div 
            className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Create Contract Template</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Template Name</label>
                  <input
                    type="text"
                    value={newTemplate.name}
                    onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm p-2 border"
                    placeholder="Enter template name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <input
                    type="text"
                    value={newTemplate.description}
                    onChange={(e) => setNewTemplate({ ...newTemplate, description: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm p-2 border"
                    placeholder="Enter template description"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Content</label>
                  
                  {/* Variable Insertion Buttons */}
                  <div className="mt-2 mb-3 p-3 bg-gray-50 border border-gray-200 rounded-md">
                    <div className="text-sm font-medium text-gray-700 mb-2">Insert Variables:</div>
                    <div className="flex flex-wrap gap-2">
                      {commonVariables.map((variable) => (
                        <button
                          key={variable.name}
                          type="button"
                          onClick={() => insertVariable(variable.name, false)}
                          className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          {variable.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="border border-gray-300 rounded-md overflow-hidden">
                    {React.createElement(ReactQuill as any, {
                      ref: (ref: any) => setNewTemplateQuillRef(ref),
                      value: newTemplate.content,
                      onChange: (content: string) => updateTemplateContent(content),
                      modules: quillModules,
                      formats: quillFormats,
                      placeholder: "Enter contract template content. Click the buttons above to insert variables.",
                      style: { minHeight: '200px' }
                    })}
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    Click the buttons above to insert variables, or manually type them like {'{{variable_name}}'}.
                  </p>
                  <style>{`
                    .ql-editor {
                      min-height: 200px;
                    }
                    .ql-toolbar {
                      background-color: #f9fafb;
                      border-bottom: 1px solid #e5e7eb;
                    }
                  `}</style>
                </div>
                
                {newTemplate.variables && newTemplate.variables.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Detected Variables</label>
                    <div className="mt-1 flex flex-wrap gap-2">
                      {newTemplate.variables.map((variable, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {variable}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_default"
                    checked={newTemplate.is_default}
                    onChange={(e) => setNewTemplate({ ...newTemplate, is_default: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_default" className="ml-2 block text-sm text-gray-900">
                    Set as default template
                  </label>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateTemplate}
                  disabled={!newTemplate.name || !newTemplate.content}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  Create Template
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Template Modal */}
      {editingTemplate && (
        <div 
          className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
          onClick={() => setEditingTemplate(null)}
        >
          <div 
            className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Contract Template</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Template Name</label>
                  <input
                    type="text"
                    value={editingTemplate.name}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, name: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm p-2 border"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <input
                    type="text"
                    value={editingTemplate.description || ''}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, description: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm p-2 border"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Content</label>
                  
                  {/* Variable Insertion Buttons */}
                  <div className="mt-2 mb-3 p-3 bg-gray-50 border border-gray-200 rounded-md">
                    <div className="text-sm font-medium text-gray-700 mb-2">Insert Variables:</div>
                    <div className="flex flex-wrap gap-2">
                      {commonVariables.map((variable) => (
                        <button
                          key={variable.name}
                          type="button"
                          onClick={() => insertVariable(variable.name, true)}
                          className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          {variable.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="border border-gray-300 rounded-md overflow-hidden">
                    {React.createElement(ReactQuill as any, {
                      ref: (ref: any) => setEditTemplateQuillRef(ref),
                      value: editingTemplate.content,
                      onChange: (content: string) => updateTemplateContent(content),
                      modules: quillModules,
                      formats: quillFormats,
                      style: { minHeight: '200px' }
                    })}
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    Click the buttons above to insert variables, or manually type them like {'{{variable_name}}'}.
                  </p>
                </div>
                
                {editingTemplate.variables && editingTemplate.variables.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Variables</label>
                    <div className="mt-1 flex flex-wrap gap-2">
                      {editingTemplate.variables.map((variable, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {variable}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="edit_is_default"
                    checked={editingTemplate.is_default}
                    onChange={(e) => setEditingTemplate({ ...editingTemplate, is_default: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="edit_is_default" className="ml-2 block text-sm text-gray-900">
                    Set as default template
                  </label>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setEditingTemplate(null)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateTemplate}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Update Template
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractTemplates;